import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Collapse,
  Snackbar,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { Layout } from '../../components/Layout';
import { AgencyFlagControl } from './AgencyFlagControl';
import AgencyEmailPanel from './AgencyEmailPanel';
import { useAgencyData } from './useAgencyData';
import { useReporterDetailData, US_STATES } from './useReporterDetailData';
import { useAuth } from '../../auth/AuthProvider';
import { AdminRole } from '../../auth/roles';

export const ReporterDetail = () => {
  const { reporterId } = useParams<{ reporterId: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const canManageAgency = hasRole('Production.Admin') || hasRole('Scheduling.Admin');
  const canManageStates = hasRole(AdminRole.SCHEDULING_ADMIN) || hasRole(AdminRole.ASSIGNER);

  const {
    reporter, reporterLoading, reporterError,
    draftPrefs, newStateToAdd, setNewStateToAdd, savingPrefs, draftValid,
    statesSnackbar, setStatesSnackbar,
    handleAddStateToDraft, handleRemoveStateFromDraft, handleSetPrimaryInDraft,
    handleAvailabilityToggle, handleSavePrefs,
  } = useReporterDetailData(reporterId!);

  const agencyData = useAgencyData(reporterId!);

  if (reporterLoading) return <Layout><Box sx={{ p: 4 }}><CircularProgress /></Box></Layout>;
  if (reporterError || !reporter) return <Layout><Box sx={{ p: 4 }}><Alert severity="error">{reporterError ?? 'Reporter not found'}</Alert></Box></Layout>;

  return (
    <Layout>
      <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton onClick={() => navigate('/reporters')} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ flex: 1 }}>{reporter.name}</Typography>
        </Box>

        {agencyData.agencyError && <Alert severity="error" sx={{ mb: 2 }}>{agencyData.agencyError}</Alert>}
        {agencyData.agencyLoading && <CircularProgress size={20} sx={{ mb: 2 }} />}

        {/* Reporter info */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography><strong>Email:</strong> {reporter.email}</Typography>
            <Typography><strong>Location:</strong> {reporter.location}</Typography>
            <Typography><strong>Address state:</strong> {reporter.reporterState}</Typography>
            {reporter.statePreferences?.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {reporter.statePreferences.map(sp => (
                  <Chip
                    key={sp.state}
                    size="small"
                    label={sp.state}
                    color={sp.isPrimary ? 'primary' : 'default'}
                    variant={sp.isPrimary ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Paper>

        {/* States section */}
        {canManageStates && (
          <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>States</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add, remove, and toggle availability. Exactly one state must be marked primary.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Autocomplete
                options={US_STATES.filter(s => !draftPrefs.some(p => p.state === s))}
                value={newStateToAdd || null}
                onChange={(_, value) => setNewStateToAdd(value || '')}
                sx={{ flex: 1 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Add state"
                    size="small"
                    style={{ marginBottom: 0 }}
                    InputLabelProps={{ ...params.InputLabelProps, sx: { '&:not(.MuiInputLabel-shrink)': { transform: 'translate(14px, 12px) scale(1)' } } }}
                  />
                )}
              />
              <Button onClick={handleAddStateToDraft} disabled={!newStateToAdd} variant="contained" startIcon={<AddIcon />} sx={{ borderRadius: 2, flexShrink: 0 }} style={{ height: 48 }}>Add</Button>
            </Box>
            {draftPrefs.length === 0 ? (
              <Alert severity="info">No states configured.</Alert>
            ) : (
              <RadioGroup
                value={draftPrefs.find(p => p.isPrimary)?.state || ''}
                onChange={(e) => handleSetPrimaryInDraft(e.target.value)}
              >
                {draftPrefs.map((pref) => (
                  <Paper
                    key={pref.state}
                    variant="outlined"
                    sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <FormControlLabel
                      value={pref.state}
                      control={<Radio size="small" />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500, minWidth: 40 }}>{pref.state}</Typography>
                          {pref.isPrimary && (
                            <Chip
                              icon={<StarIcon sx={{ fontSize: '0.85rem' }} />}
                              label="Primary"
                              size="small"
                              color="warning"
                              sx={{ height: 20 }}
                            />
                          )}
                        </Box>
                      }
                      sx={{ flex: 1, mr: 0 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={pref.virtualAvailable}
                          onChange={(e) => handleAvailabilityToggle(pref.state, 'virtualAvailable', e.target.checked)}
                        />
                      }
                      label="Virtual"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={pref.inPersonAvailable}
                          onChange={(e) => handleAvailabilityToggle(pref.state, 'inPersonAvailable', e.target.checked)}
                        />
                      }
                      label="In-person"
                    />
                    <Tooltip title="Remove state">
                      <IconButton size="small" onClick={() => handleRemoveStateFromDraft(pref.state)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Paper>
                ))}
              </RadioGroup>
            )}
            {!draftValid && (
              <Alert severity="warning" sx={{ mt: 1 }}>Select a primary state before saving.</Alert>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" onClick={handleSavePrefs} disabled={savingPrefs || !draftValid}>
                {savingPrefs ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Paper>
        )}

        {/* Agency section */}
        {canManageAgency && !agencyData.agencyLoading && (
          <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>Agency</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Authorize sub-reporters to log in and submit worksheets on behalf of this agency.
            </Typography>
            {!agencyData.agency.isAgency && (
              <AgencyFlagControl
                isAgency={agencyData.agency.isAgency}
                onEnable={agencyData.handleToggleAgency}
                onDisable={agencyData.handleToggleAgency}
              />
            )}
            <Collapse in={agencyData.agency.isAgency}>
              <Box sx={{ mt: 2 }}>
                <AgencyEmailPanel
                  agency={agencyData.agency}
                  reporterEmail={reporter.email ?? null}
                  addEmail={agencyData.addEmail}
                  setAddEmail={agencyData.setAddEmail}
                  addLoading={agencyData.addLoading}
                  addWarning={agencyData.addWarning}
                  addError={agencyData.addError}
                  onAdd={agencyData.handleAddEmail}
                  onRemove={agencyData.handleRemoveEmail}
                />
                <AgencyFlagControl
                  isAgency={agencyData.agency.isAgency}
                  onEnable={agencyData.handleToggleAgency}
                  onDisable={agencyData.handleToggleAgency}
                />
              </Box>
            </Collapse>
          </Paper>
        )}

        <Snackbar
          open={!!agencyData.snackbar}
          autoHideDuration={3000}
          onClose={() => agencyData.setSnackbar(null)}
          message={agencyData.snackbar}
        />
      </Box>

      <Snackbar
        open={statesSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setStatesSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={statesSnackbar.severity} onClose={() => setStatesSnackbar(prev => ({ ...prev, open: false }))}>
          {statesSnackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};
